/**
 * Lieferzeit-Hilfe — formatiert das ``delivery_time``-Enum-Feld eines Produkts
 * in einen menschenlesbaren Text.
 *
 *   "1-3" → "1–3 Werktage · auf Lager"
 *   "3-7" → "3–7 Werktage · wird auf Bestellung gefertigt"
 *   undefined/null → fällt auf "1-3" zurück (Standard für Alt-Bestand)
 */
export const DELIVERY_OPTIONS = [
  {
    value: '1-3',
    short: '1–3 Werktage',
    long: '1–3 Werktage · auf Lager',
    hint: 'Standard · sofort versandbereit',
    maxDays: 3,
  },
  {
    value: '3-7',
    short: '3–7 Werktage',
    long: '3–7 Werktage · wird auf Bestellung gefertigt',
    hint: 'Handgemachte Artikel, die individuell für dich neu produziert werden',
    maxDays: 7,
  },
];

const FALLBACK = DELIVERY_OPTIONS[0];

const _lookup = (value) =>
  DELIVERY_OPTIONS.find((o) => o.value === value) || FALLBACK;

export const formatDeliveryShort = (product) =>
  _lookup(product?.delivery_time).short;

export const formatDeliveryLong = (product) =>
  _lookup(product?.delivery_time).long;

export const deliveryMaxDays = (product) =>
  _lookup(product?.delivery_time).maxDays;

export const longestDeliveryFromItems = (items = []) => {
  if (!items.length) return FALLBACK;
  let worst = FALLBACK;
  for (const it of items) {
    const opt = _lookup(it?.product?.delivery_time);
    if (opt.maxDays > worst.maxDays) worst = opt;
  }
  return worst;
};

/* ---------------------------------------------------------------------------
 * Datums-Helpers: Versand- & Lieferdatum
 * Annahme: Mo-Fr sind Werktage. Samstag/Sonntag werden übersprungen.
 * - shipByDays  = maxDeliveryDays - 2 (2 Werktage für den Versand-Transit)
 * - expectedDeliveryDays = maxDeliveryDays (1 Werktag bei "1-3", bzw. 7 bei "3-7")
 * Das ergibt:
 *   "1-3"  →  Versand bis: heute+1 WT  ·  Lieferung voraussichtlich: heute+3 WT
 *   "3-7"  →  Versand bis: heute+5 WT  ·  Lieferung voraussichtlich: heute+7 WT
 * ------------------------------------------------------------------------- */

export const addWorkingDays = (date, n) => {
  const d = new Date(date);
  let added = 0;
  while (added < n) {
    d.setDate(d.getDate() + 1);
    const dow = d.getDay(); // 0 = Sonntag, 6 = Samstag
    if (dow !== 0 && dow !== 6) added += 1;
  }
  return d;
};

export const formatGermanDate = (date) =>
  new Date(date).toLocaleDateString('de-DE', {
    weekday: 'short',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });

const _shipByDaysFor = (deliveryTime) => {
  const opt = _lookup(deliveryTime);
  return Math.max(1, opt.maxDays - 2);
};

export const expectedDeliveryDate = (orderDate, deliveryTime) => {
  const opt = _lookup(deliveryTime);
  return addWorkingDays(orderDate, opt.maxDays);
};

export const shipByDate = (orderDate, deliveryTime) =>
  addWorkingDays(orderDate, _shipByDaysFor(deliveryTime));

// Spätestes erwartetes Lieferdatum für einen Cart (das langsamste Item gewinnt)
export const expectedDeliveryFromItems = (orderDate, items = []) => {
  const worst = longestDeliveryFromItems(items);
  return addWorkingDays(orderDate, worst.maxDays);
};

// Frühester Versand-Deadline für eine Order (das schnellste Item gewinnt — wir
// müssen es als erstes versendet haben, da es die kürzeste Toleranz hat)
export const earliestShipByFromOrderItems = (orderDate, orderItems = []) => {
  if (!orderItems.length) return shipByDate(orderDate, '1-3');
  let earliest = null;
  for (const it of orderItems) {
    const d = shipByDate(orderDate, it?.delivery_time || it?.product?.delivery_time || '1-3');
    if (!earliest || d < earliest) earliest = d;
  }
  return earliest;
};
